/* eslint-disable */
import React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { Box, Typography, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { useNavigate } from 'react-router-dom';

import urbanTheme from 'views/urban-dashboard/theme/urbanTheme';
import { useUrbanTopBranches } from 'hooks/useUrbanTopBranches';
import { formatIndianCurrency, formatIndianCount } from 'views/urban-dashboard/utils/formatters';
import { exportToExcel } from 'views/urban-dashboard/utils/exportToExcel';
import PerPageSelector from 'views/urban-dashboard/components/PerPageSelector';

const SORT_OPTIONS = [
    { value: 'volume', label: 'Volume', icon: <AccountBalanceWalletOutlinedIcon /> },
    { value: 'counts', label: 'Transactions', icon: <BarChartOutlinedIcon /> },
    { value: 'merchants', label: 'Merchants', icon: <StorefrontOutlinedIcon /> }
];

/**
 * UrbanTopBranches — Paginated table/list of top branches sorted by volume, counts, or merchants.
 * Matches the Urban Dashboard glassmorphic design language.
 */
function UrbanTopBranches() {
    const navigate = useNavigate();
    const { state, data, totalCount, pagination, error, sortBy, setSortBy, page, setPage, perPage, setPerPage, retry } = useUrbanTopBranches();

    return (
        <ThemeProvider theme={urbanTheme}>
            <Box
                sx={{
                    minHeight: '100vh',
                    background: 'linear-gradient(160deg, #F8FAFC 0%, #EEF2FF 35%, #F0FDF4 70%, #F8FAFC 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: '-120px',
                        right: '-80px',
                        width: '400px',
                        height: '400px',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.06) 0%, transparent 70%)',
                        pointerEvents: 'none'
                    }
                }}
            >
                <Box sx={{ position: 'relative', zIndex: 1, py: { xs: 3, sm: 4, md: 5 }, px: { xs: 2, sm: 3.5, md: 5 } }}>
                    <main style={{ maxWidth: '1200px', margin: '0 auto' }}>
                        {/* Header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
                            <Box
                                component="button"
                                onClick={() => navigate('/super-distributor/dashboard')}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 36,
                                    height: 36,
                                    borderRadius: '10px',
                                    border: '1px solid rgba(15, 23, 42, 0.08)',
                                    background: 'rgba(255, 255, 255, 0.7)',
                                    backdropFilter: 'blur(8px)',
                                    cursor: 'pointer',
                                    transition: 'all 200ms ease',
                                    outline: 'none',
                                    '&:hover': {
                                        background: 'rgba(255, 255, 255, 0.95)',
                                        borderColor: 'rgba(99, 102, 241, 0.3)'
                                    },
                                    '&:focus-visible': { outline: '2px solid #4F46E5', outlineOffset: '2px' }
                                }}
                            >
                                <ArrowBackIcon sx={{ fontSize: '18px', color: '#475569' }} />
                            </Box>
                            <Box>
                                <Typography
                                    variant="h4"
                                    component="h1"
                                    sx={{
                                        fontSize: { xs: '22px', sm: '28px' },
                                        fontWeight: 800,
                                        letterSpacing: '-0.03em',
                                        color: '#0F172A',
                                        lineHeight: 1.2
                                    }}
                                >
                                    Top Branches
                                </Typography>
                                <Typography sx={{ fontSize: '13px', color: '#64748B', mt: 0.25 }}>
                                    {totalCount > 0 ? `${totalCount} branches total` : 'Branch performance rankings'}
                                </Typography>
                            </Box>
                            {/* Export button */}
                            <ExportButton
                                onClick={() => {
                                    const endpoint = 'api/V2/moneytransfer/superDistributor/Urban/GetTopBranches';
                                    const payload = { type: sortBy, sort_by: sortBy };
                                    exportToExcel(
                                        endpoint,
                                        payload,
                                        'top_branches',
                                        ['sl_no', 'branch_name', 'distributor_id', 'volume', 'counts', 'no_of_merchants'],
                                        {
                                            sl_no: 'Rank',
                                            branch_name: 'Branch',
                                            distributor_id: 'Distributor ID',
                                            volume: 'Volume',
                                            counts: 'Transactions',
                                            no_of_merchants: 'Merchants'
                                        }
                                    );
                                }}
                                disabled={state !== 'loaded'}
                            />
                        </Box>

                        {/* Sort tabs */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                            {SORT_OPTIONS.map((opt) => (
                                <Box
                                    key={opt.value}
                                    component="button"
                                    onClick={() => setSortBy(opt.value)}
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.75,
                                        py: 0.75,
                                        borderRadius: '9px',
                                        border: '1px solid',
                                        borderColor: sortBy === opt.value ? 'rgba(99, 102, 241, 0.4)' : 'rgba(15, 23, 42, 0.06)',
                                        background: sortBy === opt.value ? 'rgba(99, 102, 241, 0.06)' : 'rgba(255, 255, 255, 0.6)',
                                        backdropFilter: 'blur(6px)',
                                        cursor: 'pointer',
                                        transition: 'all 180ms ease',
                                        outline: 'none',
                                        fontFamily: 'inherit',
                                        '&:hover': {
                                            borderColor: 'rgba(99, 102, 241, 0.25)',
                                            background: 'rgba(255, 255, 255, 0.85)'
                                        },
                                        '&:focus-visible': { outline: '2px solid #4F46E5', outlineOffset: '2px' }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', color: sortBy === opt.value ? '#4F46E5' : '#94A3B8' }}>
                                        {React.cloneElement(opt.icon, { sx: { fontSize: '15px' } })}
                                    </Box>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: '12.5px',
                                            fontWeight: sortBy === opt.value ? 700 : 500,
                                            color: sortBy === opt.value ? '#4F46E5' : '#64748B',
                                            letterSpacing: '-0.01em'
                                        }}
                                    >
                                        {opt.label}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* Content */}
                        {state === 'loading' && <LoadingSkeleton />}
                        {state === 'error' && <ErrorView error={error} onRetry={retry} />}
                        {state === 'empty' && <EmptyView />}
                        {state === 'loaded' && (
                            <>
                                <BranchList data={data} sortBy={sortBy} startRank={pagination.from} />
                                {/* Pagination + Per Page */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mt: 3 }}>
                                    <PerPageSelector value={perPage} onChange={setPerPage} />
                                    {pagination.lastPage > 1 && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 2,
                                            mt: 4
                                        }}
                                    >
                                        <IconButton
                                            disabled={page <= 1}
                                            onClick={() => setPage(page - 1)}
                                            sx={{
                                                width: 34,
                                                height: 34,
                                                borderRadius: '9px',
                                                border: '1px solid rgba(15, 23, 42, 0.08)',
                                                background: 'rgba(255,255,255,0.7)',
                                                '&:hover': { background: 'rgba(255,255,255,0.95)' }
                                            }}
                                        >
                                            <ChevronLeftIcon sx={{ fontSize: '18px' }} />
                                        </IconButton>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>
                                            {pagination.currentPage} / {pagination.lastPage}
                                        </Typography>
                                        <IconButton
                                            disabled={page >= pagination.lastPage}
                                            onClick={() => setPage(page + 1)}
                                            sx={{
                                                width: 34,
                                                height: 34,
                                                borderRadius: '9px',
                                                border: '1px solid rgba(15, 23, 42, 0.08)',
                                                background: 'rgba(255,255,255,0.7)',
                                                '&:hover': { background: 'rgba(255,255,255,0.95)' }
                                            }}
                                        >
                                            <ChevronRightIcon sx={{ fontSize: '18px' }} />
                                        </IconButton>
                                    </Box>
                                )}
                                </Box>
                            </>
                        )}
                    </main>
                </Box>
            </Box>
        </ThemeProvider>
    );
}

/** Branch list — glassmorphic rows */
function BranchList({ data, sortBy, startRank }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {data.map((branch, index) => {
                const rank = startRank + index;
                const isTop3 = rank <= 3;
                return (
                    <Box
                        key={branch.distributor_id || index}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 1.5, md: 2.5 },
                            px: { xs: 2, md: 3 },
                            py: { xs: 1.75, md: 2 },
                            borderRadius: '14px',
                            background: isTop3 ? 'rgba(255, 255, 255, 0.85)' : 'rgba(255, 255, 255, 0.65)',
                            backdropFilter: 'blur(12px)',
                            border: '1px solid',
                            borderColor: isTop3 ? 'rgba(99, 102, 241, 0.1)' : 'rgba(255, 255, 255, 0.8)',
                            boxShadow: isTop3 ? '0 2px 12px -4px rgba(99, 102, 241, 0.08)' : '0 1px 3px rgba(15, 23, 42, 0.02)',
                            transition: 'all 200ms ease',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 16px -4px rgba(15, 23, 42, 0.06)',
                                borderColor: 'rgba(99, 102, 241, 0.15)'
                            }
                        }}
                    >
                        {/* Rank */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: '8px',
                                background: isTop3
                                    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(79, 70, 229, 0.05))'
                                    : 'rgba(241, 245, 249, 0.8)',
                                flexShrink: 0
                            }}
                        >
                            {isTop3 ? (
                                <EmojiEventsOutlinedIcon sx={{ fontSize: '16px', color: '#4F46E5' }} />
                            ) : (
                                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#94A3B8' }}>{rank}</Typography>
                            )}
                        </Box>

                        {/* Branch name */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: { xs: '13px', md: '14px' },
                                    fontWeight: 600,
                                    color: '#1E293B',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {branch.branch_name || branch.Branch}
                            </Typography>
                            {branch.distributor_id && (
                                <Typography sx={{ fontSize: '11px', color: '#94A3B8', mt: 0.25 }}>ID: {branch.distributor_id}</Typography>
                            )}
                        </Box>

                        {/* Stats */}
                        <Box sx={{ display: 'flex', gap: { xs: 2, md: 4 }, alignItems: 'center', flexShrink: 0 }}>
                            <StatCell label="Volume" value={formatIndianCurrency(branch.volume || 0)} active={sortBy === 'volume'} />
                            <StatCell label="Txns" value={formatIndianCount(branch.counts || 0)} active={sortBy === 'counts'} />
                            <StatCell
                                label="Merchants"
                                value={formatIndianCount(branch.no_of_merchants || 0)}
                                active={sortBy === 'merchants'}
                                hideMobile
                            />
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

/** Compact stat cell for branch rows */
function StatCell({ label, value, active, hideMobile }) {
    return (
        <Box sx={{ textAlign: 'right', display: hideMobile ? { xs: 'none', sm: 'block' } : 'block' }}>
            <Typography
                sx={{
                    fontSize: { xs: '13px', md: '14px' },
                    fontWeight: active ? 700 : 600,
                    color: active ? '#4F46E5' : '#1E293B',
                    fontFeatureSettings: '"tnum" on',
                    whiteSpace: 'nowrap'
                }}
            >
                {value}
            </Typography>
            <Typography sx={{ fontSize: '10px', color: '#94A3B8', fontWeight: 500, mt: 0.1 }}>{label}</Typography>
        </Box>
    );
}

/** Loading skeleton */
function LoadingSkeleton() {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {Array.from({ length: 6 }, (_, i) => (
                <Box
                    key={i}
                    sx={{
                        height: '60px',
                        borderRadius: '14px',
                        background: 'rgba(255, 255, 255, 0.5)',
                        border: '1px solid rgba(255, 255, 255, 0.7)',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        animationDelay: `${i * 0.08}s`,
                        '@keyframes pulse': {
                            '0%, 100%': { opacity: 0.5 },
                            '50%': { opacity: 1 }
                        }
                    }}
                />
            ))}
        </Box>
    );
}

/** Error view */
function ErrorView({ error, onRetry }) {
    return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography sx={{ fontSize: '15px', color: '#64748B', mb: 2 }}>{error?.message || 'Something went wrong.'}</Typography>
            <Box
                component="button"
                onClick={onRetry}
                sx={{
                    px: 3,
                    py: 1,
                    borderRadius: '10px',
                    border: '1px solid rgba(99, 102, 241, 0.3)',
                    background: 'rgba(99, 102, 241, 0.05)',
                    color: '#4F46E5',
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'all 200ms ease',
                    '&:hover': { background: 'rgba(99, 102, 241, 0.1)' },
                    '&:focus-visible': { outline: '2px solid #4F46E5', outlineOffset: '2px' }
                }}
            >
                Retry
            </Box>
        </Box>
    );
}

/** Empty view */
function EmptyView() {
    return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
            <EmojiEventsOutlinedIcon sx={{ fontSize: 48, color: '#CBD5E1', mb: 2 }} />
            <Typography sx={{ fontSize: '15px', color: '#64748B' }}>No branch data available yet.</Typography>
        </Box>
    );
}

/** Vercel-style export button */
function ExportButton({ onClick, disabled }) {
    return (
        <Box
            component="button"
            onClick={onClick}
            disabled={disabled}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.75,
                py: 0.85,
                ml: 'auto',
                borderRadius: '9px',
                border: '1px solid rgba(15, 23, 42, 0.08)',
                background: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(8px)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                fontFamily: 'inherit',
                outline: 'none',
                transition: 'all 200ms ease',
                '&:hover:not(:disabled)': {
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderColor: 'rgba(99, 102, 241, 0.3)',
                    boxShadow: '0 2px 8px -2px rgba(99, 102, 241, 0.1)'
                },
                '&:focus-visible': { outline: '2px solid #4F46E5', outlineOffset: '2px' }
            }}
        >
            <FileDownloadOutlinedIcon sx={{ fontSize: '16px', color: '#4F46E5' }} />
            <Typography component="span" sx={{ fontSize: '12.5px', fontWeight: 600, color: '#1E293B', whiteSpace: 'nowrap' }}>
                Export
            </Typography>
        </Box>
    );
}

export default UrbanTopBranches;
